import { CheckCircleIcon, PlusIcon, TrashIcon, XCircleIcon } from '@heroicons/react/24/outline';
import {isEmpty} from 'radash';
import {useState} from 'react';
import {Controller, useFieldArray, useFormContext} from 'react-hook-form';

import {Button} from '@/components/core/Button';
import {Checkbox} from '@/components/core/Checkbox';
import {Icon} from '@/components/core/Icon';
import {Loader} from '@/components/core/Loader';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/core/Select';
import {useTranslation} from '@/hooks/useTranslation';

import {useEnhancedContentRetailers} from '../enhanced-content.queries';
import { ArticleNumbers } from './ArticleNumbers';

const DestinationsTab = ({ isReviewer } : { isReviewer: boolean }) => {

    const { t } = useTranslation();

    const [selectedDestinations, setSelectedDestinations] = useState<Set<number>>(new Set());

    const { data: availableRetailers, isLoading } = useEnhancedContentRetailers();

    const { control, register } = useFormContext();
    const { fields, append, remove } = useFieldArray({
        control: control,
        name: 'retailerLinkIds'
    });

    if (isLoading) {
        return (
            <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
                <Loader />
            </div>
        );
    }

    if (!availableRetailers || isEmpty(availableRetailers)) {
        return ;
    }

    return (
        <div className="tw-flex tw-flex-col tw-h-full tw-px-6">
            {!isReviewer && <div className="tw-flex">
                <Button onClick={() => append({ articleNumbers: [{articleNumber: ''}], name: '' })}>
                    <div className='tw-flex tw-items-center tw-justify-center'>
                        <Icon icon={PlusIcon} variant="light" size="sm" />
                        <span className="tw-ml-1 tw-leading-none tw-align-middle">{t('ENHANCED_CONTENT.ADD_DESTINATION')}</span>
                    </div>
                </Button>

                {

                    selectedDestinations.size > 0 ?
                        <Button
                            className="tw-ml-auto"
                            variant="secondary"
                            onClick={() => setSelectedDestinations(new Set())}
                        >
                            <div className='tw-flex tw-items-center tw-justify-center'>
                                <Icon icon={XCircleIcon} variant="secondary" size="sm" />
                                <span className="tw-ml-1 tw-leading-none tw-align-middle">{t('ENHANCED_CONTENT.DESELECT_ALL')}</span>
                            </div>
                        </Button>
                        :
                        <Button
                            className="tw-ml-auto"
                            variant="secondary"
                            onClick={() => {
                                // Create a Set from an array length as we are tracking what's selected by
                                // index of the destination entry.
                                setSelectedDestinations(new Set(Array.from({ length: fields.length }, (_, index) => index)));
                            }}
                        >
                            <div className='tw-flex tw-items-center tw-justify-center'>
                                <Icon icon={CheckCircleIcon} variant="secondary" size="sm" />
                                <span className="tw-ml-1 tw-leading-none tw-align-middle">{t('ENHANCED_CONTENT.SELECT_ALL')}</span>
                            </div>
                        </Button>

                }

                <Button
                    className="tw-ml-1"
                    onClick={() => {
                        remove([...selectedDestinations]);
                        setSelectedDestinations(new Set());
                    }}
                    variant="destructive"
                    disabled={selectedDestinations.size === 0}
                >
                    <div className='tw-flex tw-items-center tw-justify-center'>
                        <Icon icon={TrashIcon} variant="destructive" disabled={selectedDestinations.size === 0} size="sm" />
                        <span className="tw-ml-1 tw-leading-none tw-align-middle">{t('ENHANCED_CONTENT.DELETE_SELECTED')}</span>
                    </div>
                </Button>
            </div>}

            <div className="tw-flex tw-flex-col tw-p-4 tw-overflow-y-auto">
                {fields.map((field, index) =>
                    <div key={field.id} className="tw-flex tw-flex-col sm:tw-flex-row tw-w-full tw-p-4 tw-mb-4 tw-bg-white tw-shadow-sm tw-rounded-lg">
                        <Checkbox
                            className="tw-mb-4 sm:tw-mr-4 sm:tw-mb-0"
                            isChecked={selectedDestinations.has(index)}
                            onCheckedChange={() => {
                                if (selectedDestinations.has(index)) {
                                    selectedDestinations.delete(index);
                                    setSelectedDestinations(new Set([...selectedDestinations]));
                                } else {
                                    setSelectedDestinations(new Set([...selectedDestinations, index]));
                                }
                            }}
                            disabled={isReviewer}/>
                        <div className="tw-flex tw-flex-col tw-w-full">
                            <div className="tw-flex tw-w-full tw-mb-4 tw-items-center">
                                <Controller
                                    control={control}
                                    name={`retailerLinkIds.${index}.name` as const}
                                    rules={{
                                        required: true,
                                    }}
                                    render={({field: {value, onChange}}) =>
                                        <Select value={value} onValueChange={onChange} disabled={isReviewer}>
                                            <SelectTrigger className="tw-w-full sm:tw-w-2/5">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {availableRetailers?.map((retailer) =>
                                                    <SelectItem key={retailer.name}
                                                        value={retailer.name}>
                                                        {retailer.label}
                                                    </SelectItem>
                                                )}
                                            </SelectContent>
                                        </Select>}/>
                            </div>
                            <ArticleNumbers control={control}
                                destinationIndex={index}
                                availableRetailers={availableRetailers}
                                register={register}
                                isReviewer= {isReviewer}/>

                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export { DestinationsTab };
